/**
 * Courbe d'évolution de la note d'ÉQUIPE match par match (SVG pur, série unique).
 * Calquée sur la courbe de notes du joueur (playerProfile.ts:ratingCurve) et
 * réutilise ses styles .pp-curve/.pp-cv-*. L'axe X = adversaire (oppCode résolu
 * côté serveur, fiable même en phase finale).
 */
import { ratingTrend, type TeamRatingHistory } from "../../app/teamRatings.ts";
import { t } from "../../app/i18n.ts";
import { esc } from "../html.ts";
import { teamRateCls } from "../teamRatingTag.ts";

const trendMeta = (): Record<"up" | "down" | "flat", { ic: string; lbl: string }> => ({
  up: { ic: "ti-trending-up", lbl: t("fiche.teamRating.curveTrend.up") },
  down: { ic: "ti-trending-down", lbl: t("fiche.teamRating.curveTrend.down") },
  flat: { ic: "ti-minus", lbl: t("fiche.teamRating.curveTrend.flat") },
});

export function teamRatingCurve(hist: TeamRatingHistory): string {
  const pts = hist.matches
    .map((m) => ({ opp: m.oppCode, r: m.rating }))
    .filter((p): p is { opp: string | null; r: number } => p.r != null);
  if (pts.length === 0) return "";
  const W = 300;
  const H = 132;
  const pad = { l: 10, r: 10, t: 20, b: 26 };
  const rs = pts.map((p) => p.r);
  const lo = Math.max(4, Math.floor(Math.min(...rs) - 0.4));
  const hi = Math.min(10, Math.ceil(Math.max(...rs) + 0.4));
  const span = Math.max(0.5, hi - lo);
  const n = pts.length;
  const sx = (i: number): number => (n === 1 ? W / 2 : pad.l + (i / (n - 1)) * (W - pad.l - pad.r));
  const sy = (r: number): number => pad.t + ((hi - r) / span) * (H - pad.t - pad.b);
  const xy = pts.map((p, k) => ({ ...p, x: sx(k), y: sy(p.r) }));
  const line = xy.map((p, k) => `${k === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const avg = rs.reduce((a, b) => a + b, 0) / rs.length;
  const avgY = sy(avg).toFixed(1);
  const dots = xy
    .map(
      (p) =>
        `<circle class="pp-cv-dot ${teamRateCls(p.r)}" cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="4"/>` +
        `<text class="pp-cv-val" x="${p.x.toFixed(1)}" y="${(p.y - 9).toFixed(1)}" text-anchor="middle">${p.r.toFixed(1)}</text>` +
        `<text class="pp-cv-x" x="${p.x.toFixed(1)}" y="${H - 8}" text-anchor="middle">${esc(p.opp ?? "")}</text>`,
    )
    .join("");
  const trend = ratingTrend(pts.map((p) => p.r));
  const tm = trendMeta()[trend];
  return `<div class="cp-block-h">${t("fiche.teamRating.curve.title")} <span class="tr-trend ${trend}" title="${t("fiche.teamRating.curve.trendTitle")}"><i class="ti ${tm.ic}" aria-hidden="true"></i> ${tm.lbl}</span></div>
    <svg viewBox="0 0 ${W} ${H}" class="pp-curve" role="img" aria-label="${t("fiche.teamRating.curve.ariaLabel")}">
      <line class="pp-cv-avg" x1="${pad.l}" y1="${avgY}" x2="${W - pad.r}" y2="${avgY}"/>
      <text class="pp-cv-avgt" x="${W - pad.r}" y="${(Number(avgY) - 4).toFixed(1)}" text-anchor="end">${t("common.avgAbbr")} ${avg.toFixed(2)}</text>
      ${n > 1 ? `<path class="pp-cv-line" d="${line}"/>` : ""}
      ${dots}
    </svg>`;
}
